/* eslint-disable @typescript-eslint/no-explicit-any */
import io from 'socket.io-client';
import { Notification } from '@eds/vanilla';
import { ARGO_API_URL } from '../config';
import keycloak from './keycloak';

class WebSocketService {
  private socket: any;
  private readonly url: string = '';
  // RLCS-1593 — service mesh / API gateway URL migration.
  // Removed the hardcoded fallback `|| 'https://bff.swob.183295432136.eu-west-1.ac.ericsson.se'`
  // for the same reason as src/api/axiosAdminInstance.tsx: it pinned the
  // WebSocket origin to one specific cluster. Unreachable today, but it would
  // activate on the same-origin cutover and silently point live notifications
  // at a different cluster. See RLCS-1587 / RLCS-1593.
  private baseUrl = ARGO_API_URL;
  private onErrorCallback?: (message: string) => void;
  static instance: any = null;

  constructor() {
    if (WebSocketService.instance) {
      return WebSocketService.instance; // enforce singleton
    }
    // RLCS-1587 — join the base URL and the namespace without producing a
    // double slash. At the same-origin cutover the base URL becomes '/', and
    // `${'/'}/events` yields '//events'. socket.io-client treats a leading '//'
    // as a protocol-relative URL and rewrites it to `<protocol>//events`, i.e.
    // host 'events', which does not resolve — live notifications would fail
    // with no obvious cause. Stripping any trailing slash yields '/events':
    // same origin, '/events' namespace, and the handshake stays on socket.io's
    // default '/socket.io/' transport path, which is the prefix the gateway's
    // rlca-routes VirtualService forwards to the BFF.
    // Absolute base URLs are unaffected ('https://host' and 'https://host/'
    // both give 'https://host/events').
    this.url = `${this.baseUrl.replace(/\/+$/, '')}/events`;
    WebSocketService.instance = this;
  }

  // Connect to the WebSocket server with authentication
  connect(onError?: (message: string) => void) {
    if (onError) this.onErrorCallback = onError;
    if (this.socket?.connected) return this.socket;
    this.socket = io(this.url, {
      auth: {
        token: keycloak.token ? `${keycloak.token}` : ''
      },
      transports: ['polling', 'websocket'],
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    this.setupEventListeners();
    return this.socket;
  }

  // Disconnect from the WebSocket server
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  // Setup default event listeners
  private setupEventListeners() {
    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
    });

    this.socket.on('disconnect', () => {
      console.log('Disconnected from WebSocket server');
    });

    this.socket.on('error', async (error: any) => {
      if (error && error.message === 'JWT authentication failed: Invalid token') {
        try {
          await keycloak.updateToken(5);
          this.disconnect();
          this.connect();
        } catch {
          this.onErrorCallback?.('Token refresh failed.');
        }
      } else {
        const notification = new Notification({
          title: 'Websocket Error',
          description: `${error.message || 'An error occurred with the websocket.'}`,
          icon: 'icon-triangle-warning',
          stripeColor: 'red',
          timeout: 5000,
          action: () => {
            console.error('WebSocket error:', error);
          }
        });
        notification.init();
        this.disconnect();
      }
    });

    this.socket.on('connection_success', (data: any) => {
      console.log('WebSocket connection successful:', data);
    });
  }

  // Listen for database changes
  onDatabaseChange(callback: (data: any) => void) {
    this.socket?.on('db-change', callback);
    return () => this.socket?.off('db-change', callback);
  }

  // Listen for specific entity changes
  onEntityChange(entityType: string, callback: (data: any) => void) {
    const handler = (data: any) => {
      if (data.entity.toLowerCase() === entityType.toLowerCase()) {
        callback(data);
      }
    };

    this.socket?.on('db-change', handler);
    return () => this.socket?.off('db-change', handler);
  }

  // Listen for user notifications
  onNotification(callback: (data: any) => void) {
    this.socket?.on('notification', callback);
    return () => this.socket?.off('notification', callback);
  }
}

const socketService = new WebSocketService();

export default socketService;
